// ════════════════════════════════════════════════════════════════════════
//  Welcher Lehrgang ist noch zu, und warum
//
//  Reine Rechnung aus dem, was die Anmeldung liefert – kein Zustand, keine
//  Abfrage. Die Zahlen kommen vom Server (`stages`, `done`, `requires`), weil
//  nur er weiß, wie viele Stufen eine Fassung hat, ohne sie zu laden.
//
//  Abgeschlossen heißt: jede Stufe mindestens einmal geübt. Bewusst niedrig –
//  wer eine Zeile nur mit einem Stern schafft, kommt trotzdem weiter. Ein Kind
//  dauerhaft auszusperren, weil ihm eine Zeile nicht gelingt, wäre das Gegenteil
//  von dem, was dieser Lehrgang will; wie gut es lief, sieht die Lehrkraft in
//  der Ergebnisübersicht.
// ════════════════════════════════════════════════════════════════════════

import type { Course } from "../../../shared/lib/backend.js";

/**
 * Nicht `Lock`: das ist ein DOM-Typ (Web Locks API) und global sichtbar. Ein
 * vergessener Import fällt dann nicht auf, sondern ergibt eine Fehlermeldung
 * über Felder, die niemand geschrieben hat.
 */
export type CourseLock = {
  /** Titel des Lehrgangs, der zuerst dran ist. */
  readonly after: string;
  /** Wie viele Übungen dort noch fehlen. Mindestens 1. */
  readonly missing: number;
};

/** Alle Stufen mindestens einmal geübt. */
export function isDone(course: Course): boolean {
  return course.stages > 0 && course.done >= course.stages;
}

/**
 * Ist dieser Lehrgang noch zu?
 *
 * Null heißt offen. Zu ist er nur, wenn sein Vorgänger **in derselben Auswahl
 * steht** und dort noch etwas fehlt. Steht der Vorgänger nicht in der Liste –
 * die Klasse führt ihn nicht, oder die Lizenz deckt ihn nicht ab –, bleibt
 * dieser Lehrgang offen: sonst stünde das Kind vor einer Tür, hinter der es
 * nichts zu üben gibt, und dem Hinweis auf einen Lehrgang, den es gar nicht hat.
 */
export function lockOf(course: Course, all: readonly Course[]): CourseLock | null {
  if (course.requires === null) return null;
  const before = all.find((c) => c.slug === course.requires);
  if (!before || isDone(before)) return null;
  return { after: before.title, missing: Math.max(1, before.stages - before.done) };
}

/** Die Lehrgänge, die das Kind jetzt wählen darf. */
export function openCourses(all: readonly Course[]): Course[] {
  return all.filter((course) => lockOf(course, all) === null);
}
